export type OptionGroupPriceMode = "sum" | "highest" | "average";
export type OptionGroupOptionType = "personalizado" | "ingredientes";

export interface ValidatedOption {
  /** Identidade do grupo/opção no cardápio (quando o produto já tem id). */
  groupId?: string;
  optionId?: string;
  groupName: string;
  optionName: string;
  price: number;
  ingredientId?: string | null;
  /** Posição do grupo dentro de `optionGroups`. Ver `resolveGroupIndex`. */
  groupIndex?: number;
}

export interface OptionGroup {
  /** Identidade estável do grupo no jsonb (o servidor gera quando falta). */
  id?: string;
  name: string;
  required?: boolean;
  selectionType?: "single" | "multiple";
  maxSelections?: number | null;
  // Piso de escolhas do grupo (default 1 = "obrigatório" clássico). Combo que
  // exige os 5 sabores põe min=5: sem isso "obrigatório" só garantia UMA
  // escolha e o pedido entrava incompleto (Pedro, 25/08/2026). Medido antes de
  // mexer: 40,8% dos grupos com teto ≥2 são preenchidos com MENOS que o teto,
  // então "até N" segue valendo — o piso é opt-in por grupo.
  minSelections?: number | null;
  priceMode?: OptionGroupPriceMode | null;
  optionType?: OptionGroupOptionType | null;
  // "multiple" com cada opção no máximo 1 vez (UI vira liga/desliga).
  onePerOption?: boolean | null;
  // LEGADO (primeiro «Contar preparos», 15/10/2026): o peso era do grupo. Hoje o peso é de cada opção (`countAs`);
  // este só vale para a opção sem o próprio, até o produto ser salvo de novo (o editor e a Configuradora migram).
  prepWeight?: number | null;
  // Pacote de escolhas (15/10/2026, caso Meu macarrão): false = este grupo NÃO conta no pacote (o «Tipo de macarrão» e o
  // «Molho» seguem com o próprio limite; só os «Ingredientes» contam). Ausente = conta, como sempre.
  inPackage?: boolean | null;
  options?: OptionGroupOption[];
}

export interface OptionGroupOption {
  id?: string;
  name: string;
  price?: number | null;
  ingredientId?: string | null;
  stock?: number | null;
  // Adicionais fracionados (Pedro, 15/10/2026; recurso do Profissional): quanto a opção conta no limite do grupo e no
  // pacote de escolhas — ½ ou 2 (ausente = 1). Sem o recurso a loja vende como se fosse 1 (`semPesos`).
  countAs?: number | null;
  // Peso guardado com o interruptor do produto desligado: não conta (só o editor lê, para religar como era).
  countAsOff?: number | null;
  // Pacote de escolhas (Pedro, 29/10/2026, Açaí beat): false = este adicional, numa lista que conta, cobra o preço dele e
  // NÃO conta no pacote (inclusos, extras, máximo, obrigatório e contador). Ausente = conta, como a lista dele.
  inPackage?: boolean | null;
}

export interface OptionSelectionInput {
  groupName: string;
  optionName: string;
  /** Posição do grupo dentro de `optionGroups`. Ver `resolveGroupIndex`. */
  groupIndex?: number | null;
}

/**
 * Chave de comparação de nome: sem acento, sem caixa, sem espaço sobrando.
 * O carrinho fica salvo no navegador do cliente e o lojista edita o cardápio
 * no meio do caminho — "Nutella" vs "NUTELLA" é a MESMA opção pra quem pede.
 * Antes disso o nome tinha que bater byte a byte e a diferença sumia o
 * adicional em silêncio (incidente Oxe Doceria, 25/08/2026).
 */
export function foldOptionName(raw: string | null | undefined): string {
  return Array.from(String(raw ?? "").normalize("NFD"))
    .filter((ch) => {
      const c = ch.codePointAt(0) ?? 0;
      return c < 0x300 || c > 0x36f;
    })
    .join("")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function findOption(
  group: OptionGroup | undefined,
  optionName: string,
): OptionGroupOption | undefined {
  const exato = group?.options?.find((o) => o.name === optionName);
  if (exato) return exato;
  const alvo = foldOptionName(optionName);
  return group?.options?.find((o) => foldOptionName(o.name) === alvo);
}

function groupHasOption(group: OptionGroup | undefined, optionName: string): boolean {
  return !!findOption(group, optionName);
}

function sameGroupName(a: string | null | undefined, b: string | null | undefined): boolean {
  return a === b || foldOptionName(a) === foldOptionName(b);
}

/** Piso efetivo do grupo: nunca maior que o teto (config incoerente não trava
 * o cliente num alvo impossível). */
export function groupMinSelections(group: OptionGroup | undefined): number {
  if (!group || group.selectionType === "single") return group?.required ? 1 : 0;
  const raw = Number(group.minSelections);
  if (!Number.isFinite(raw) || raw <= 1) return group.required ? 1 : 0;
  const teto = Number(group.maxSelections);
  return Number.isFinite(teto) && teto > 0 ? Math.min(Math.trunc(raw), teto) : Math.trunc(raw);
}

function groupCapacity(group: OptionGroup): number {
  if (group.selectionType === "single") return 1;
  return group.maxSelections || Number.POSITIVE_INFINITY;
}

/**
 * O nome do grupo NÃO identifica o grupo: o lojista repete o mesmo título pra
 * montar vários passos iguais ("Acompanhamentos" 4x, um pra arroz, um pra
 * feijão...). Medido em 30/07/2026: 136 produtos de 70 lojas, e em 73 desses
 * pares os homônimos ainda repetem o nome de alguma opção.
 *
 * Como a seleção viaja como (groupName, optionName), procurar o grupo só pelo
 * nome acertava sempre o PRIMEIRO homônimo: a escolha feita nos outros grupos
 * ou era descartada em silêncio ou virava N cópias da opção do primeiro (foi o
 * "+ 4x Arroz Branco" que os lojistas reportaram).
 *
 * O cliente manda `groupIndex`. Quem não manda (carrinho salvo no navegador
 * antes deste fix, pedido antigo sendo reeditado) cai na distribuição gulosa:
 * o primeiro homônimo que TEM a opção e ainda não encheu a cota.
 */
function resolveGroupIndex(
  groups: OptionGroup[],
  sel: { groupName: string; optionName: string; groupIndex?: number | null },
  used: number[],
): number {
  const hinted = sel.groupIndex;
  if (typeof hinted === "number" && hinted >= 0 && hinted < groups.length) {
    const g = groups[hinted];
    if (sameGroupName(g?.name, sel.groupName) && groupHasOption(g, sel.optionName)) return hinted;
  }
  let firstMatch = -1;
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    if (!g || !sameGroupName(g.name, sel.groupName) || !groupHasOption(g, sel.optionName)) continue;
    if (firstMatch < 0) firstMatch = i;
    if (used[i] < groupCapacity(g)) return i;
  }
  return firstMatch;
}

// Aplica o priceMode do grupo. "highest" cobra só a opção mais cara do grupo,
// "average" cobra a média (arredondada pra cima ao centavo pra não perder receita),
// "sum" (ou ausente) soma tudo — comportamento antes de mai/2026.
function applyGroupPriceMode(
  mode: OptionGroupPriceMode | null | undefined,
  prices: number[],
): number {
  if (prices.length === 0) return 0;
  if (mode === "highest") return Math.max(...prices);
  if (mode === "average") {
    const sum = prices.reduce((s, p) => s + p, 0);
    return Math.ceil((sum / prices.length) * 100) / 100;
  }
  return prices.reduce((s, p) => s + p, 0);
}

/**
 * Re-prices client-selected options against the product's authoritative
 * option catalog. O que NÃO casa volta em `unmatched` — quem chama decide
 * (o link público recusa o pedido; salvar diferente do que o cliente pediu é
 * pior que recusar).
 * Aplica o priceMode de cada grupo ao calcular `optionsTotal`. `validated[].price`
 * mantém o preço unitário declarado (pro recibo / exibição).
 */
export function pickAndPriceOptions(
  productGroups: OptionGroup[] | null | undefined,
  selected: OptionSelectionInput[] | null | undefined,
): { validated: ValidatedOption[]; optionsTotal: number; unmatched: OptionSelectionInput[] } {
  const groups = productGroups || [];
  const validated: ValidatedOption[] = [];
  const unmatched: OptionSelectionInput[] = [];
  const used = groups.map(() => 0);
  const pricesByGroup = new Map<number, number[]>();
  if (Array.isArray(selected)) {
    for (const opt of selected) {
      const gi = resolveGroupIndex(groups, opt, used);
      if (gi < 0) {
        unmatched.push(opt);
        continue;
      }
      const group = groups[gi];
      const dbOpt = findOption(group, opt.optionName);
      if (!dbOpt) {
        unmatched.push(opt);
        continue;
      }
      const price = Number(dbOpt.price) || 0;
      used[gi]++;
      validated.push({
        // Identidade (id) E snapshot (nome) juntos: o id casa estoque/preço
        // com o cardápio de hoje, o nome mantém o pedido antigo LEGÍVEL quando
        // a opção for renomeada ou apagada. Um não substitui o outro.
        ...(group.id ? { groupId: group.id } : {}),
        ...(dbOpt.id ? { optionId: dbOpt.id } : {}),
        groupName: group.name,
        // Nome CANÔNICO do produto (não o que o client mandou): o cupom e o
        // painel mostram o cardápio de hoje, não a grafia do carrinho velho.
        optionName: dbOpt.name,
        price,
        ingredientId: dbOpt.ingredientId ?? null,
        groupIndex: gi,
      });
      const bucket = pricesByGroup.get(gi) || [];
      bucket.push(price);
      pricesByGroup.set(gi, bucket);
    }
  }
  let optionsTotal = 0;
  for (const [gi, prices] of pricesByGroup) {
    optionsTotal += applyGroupPriceMode(groups[gi]?.priceMode, prices);
  }
  return { validated, optionsTotal, unmatched };
}

/**
 * Calcula `optionsTotal` no cliente (cart hooks). Mesma lógica de
 * `pickAndPriceOptions` mas aceita opções já validadas (com `groupName` e `price`).
 */
export function computeOptionsTotal(
  productGroups: OptionGroup[] | null | undefined,
  selected:
    | Array<{ groupName: string; price: number; optionName?: string; groupIndex?: number | null }>
    | null
    | undefined,
): number {
  if (!Array.isArray(selected) || selected.length === 0) return 0;
  const groups = productGroups || [];
  const used = groups.map(() => 0);
  // Grupo não encontrado (produto sem optionGroups carregado) cai num balde
  // próprio por nome e soma — nunca some do total do carrinho.
  const buckets = new Map<string, { mode: OptionGroupPriceMode | null | undefined; prices: number[] }>();
  for (const opt of selected) {
    // Sem `optionName` não dá pra distinguir homônimos — cai no primeiro, que
    // é o comportamento antigo (só o priceMode depende disso).
    const gi = opt.optionName
      ? resolveGroupIndex(groups, { ...opt, optionName: opt.optionName }, used)
      : groups.findIndex((g) => g.name === opt.groupName);
    if (gi >= 0) used[gi]++;
    const key = gi >= 0 ? `#${gi}` : `?${opt.groupName}`;
    const bucket = buckets.get(key) || { mode: groups[gi]?.priceMode, prices: [] };
    bucket.prices.push(opt.price);
    buckets.set(key, bucket);
  }
  let total = 0;
  for (const { mode, prices } of buckets.values()) {
    total += applyGroupPriceMode(mode, prices);
  }
  return total;
}

const pesoValido = (w: unknown): number | null => (Number(w) === 0.5 || Number(w) === 2 ? Number(w) : null);

/**
 * Quanto a opção conta (Adicionais fracionados, Pedro 15/10/2026): ½, 1 ou 2 — no limite do próprio grupo e no total
 * do pacote de escolhas. O peso é da opção; o do grupo (`prepWeight`, legado) só vale para a opção sem o próprio.
 */
export function optionWeight(group: OptionGroup | undefined, optionName: string): number {
  return pesoValido(findOption(group, optionName)?.countAs) ?? pesoValido(group?.prepWeight) ?? 1;
}

/** O grupo tem alguma opção que não conta 1. */
export function groupHasWeights(group: OptionGroup | undefined): boolean {
  if (pesoValido(group?.prepWeight) != null) return true;
  return (group?.options || []).some((o) => pesoValido(o?.countAs) != null);
}

/** Quanto as escolhas do grupo somam (o "3½ de 4"): opção marcada 2 vezes conta 2 × o peso. */
export function groupWeightedCount(group: OptionGroup | undefined, optionNames: string[]): number {
  return optionNames.reduce((s, n) => s + optionWeight(group, n), 0);
}

/**
 * Os grupos sem os pesos (todas as opções contam 1): a loja sem o recurso «Adicionais fracionados» — plano ou
 * liberação vencida — vende como antes, e o peso fica guardado no cardápio.
 */
export function semPesos<G extends OptionGroup>(groups: G[] | null | undefined): G[] {
  return (groups || []).map((g) => {
    if (!groupHasWeights(g)) return g;
    const { prepWeight: _legado, ...resto } = g;
    return {
      ...resto,
      options: (g.options || []).map((o) => {
        if (pesoValido(o?.countAs) == null) return o;
        const { countAs: _peso, ...semPeso } = o;
        return semPeso;
      }),
    } as G;
  });
}

/** Quantidade com meio: 8.5 → "8½"; 0.5 → "½"; 7 → "7". */
export function formatQtd(n: number): string {
  const inteiro = Math.floor(n + 1e-9);
  const meio = n - inteiro > 0.25;
  if (!meio) return String(inteiro);
  return inteiro === 0 ? "½" : `${inteiro}½`;
}

/**
 * Total do pacote de escolhas (o "X") de um item: a soma do peso de cada escolha — opção marcada 2 vezes conta
 * 2 × o peso. Liga a escolha ao grupo como o preço (`resolveGroupIndex`); escolha que não casa com o cardápio não
 * conta (o servidor também a descarta), nem a de grupo fora do pacote (`inPackage: false`).
 */
export function countPreparos(
  productGroups: OptionGroup[] | null | undefined,
  selected: Array<{ groupName: string; optionName: string; groupIndex?: number | null }> | null | undefined,
): number {
  if (!Array.isArray(selected) || selected.length === 0) return 0;
  const groups = productGroups || [];
  const used = groups.map(() => 0);
  let total = 0;
  for (const sel of selected) {
    const gi = resolveGroupIndex(groups, sel, used);
    if (gi < 0) continue;
    used[gi]++;
    if (!optionInPackage(groups[gi], sel.optionName)) continue;
    total += optionWeight(groups[gi], sel.optionName);
  }
  return total;
}

/** A escolha conta no pacote: nem a lista nem o adicional estão marcados fora (`inPackage: false`). */
export function optionInPackage(group: OptionGroup | null | undefined, optionName: string): boolean {
  if (group?.inPackage === false) return false;
  return findOption(group ?? undefined, optionName)?.inPackage !== false;
}

/** O mesmo total de `countPreparos`, separado por grupo (posição → soma dos pesos); grupo fora do pacote fica 0. Para o
 *  pacote que conta cada lista separado (`porLista`). */
export function countPreparosPorGrupo(
  productGroups: OptionGroup[] | null | undefined,
  selected: Array<{ groupName: string; optionName: string; groupIndex?: number | null }> | null | undefined,
): number[] {
  const groups = productGroups || [];
  const porGrupo = groups.map(() => 0);
  if (!Array.isArray(selected) || selected.length === 0) return porGrupo;
  const used = groups.map(() => 0);
  for (const sel of selected) {
    const gi = resolveGroupIndex(groups, sel, used);
    if (gi < 0) continue;
    used[gi]++;
    if (!optionInPackage(groups[gi], sel.optionName)) continue;
    porGrupo[gi] += optionWeight(groups[gi], sel.optionName);
  }
  return porGrupo;
}

/** Chave do grupo no estado de seleção da UI: a POSIÇÃO, nunca o nome. */
export function optionGroupKey(index: number): string {
  return String(index);
}

/**
 * Opções já escolhidas → estado da UI (`{ [posição do grupo]: [opções] }`).
 * Usado ao reabrir um item do carrinho/pedido pra editar: o item guarda só
 * (groupName, optionName), então o grupo é reencontrado por `resolveGroupIndex`.
 */
export function selectionsByGroupIndex(
  productGroups: OptionGroup[] | null | undefined,
  selected:
    | Array<{ groupName: string; optionName: string; groupIndex?: number | null }>
    | null
    | undefined,
): Record<string, string[]> {
  const groups = productGroups || [];
  const used = groups.map(() => 0);
  const out: Record<string, string[]> = {};
  for (const sel of selected || []) {
    const gi = resolveGroupIndex(groups, sel, used);
    if (gi < 0) continue;
    used[gi]++;
    const key = optionGroupKey(gi);
    (out[key] ||= []).push(sel.optionName);
  }
  return out;
}

/** Estado da UI → lista achatada pra `pickAndPriceOptions`, já com o índice. */
export function flattenGroupSelections(
  productGroups: OptionGroup[] | null | undefined,
  selectionsByGroup: Record<string, string[]>,
): OptionSelectionInput[] {
  const groups = productGroups || [];
  const flat: OptionSelectionInput[] = [];
  for (let i = 0; i < groups.length; i++) {
    for (const optionName of selectionsByGroup[optionGroupKey(i)] || []) {
      flat.push({ groupName: groups[i].name, optionName, groupIndex: i });
    }
  }
  return flat;
}

/**
 * Server-side defense for the option-group cardinality rules the UI enforces.
 * Returns the first violation message or null when all groups are valid.
 */
export function validateOptionGroupCardinality(
  productGroups: OptionGroup[] | null | undefined,
  validated: ValidatedOption[],
  productName: string,
): string | null {
  const groups = productGroups || [];
  // Contagem por GRUPO, não por nome de grupo: com homônimos, contar por nome
  // somava as escolhas dos 4 "Acompanhamentos" num balde só e estourava o
  // maxSelections de cada um. Ver `resolveGroupIndex`.
  const counts = groups.map(() => 0);
  // Adicionais fracionados: o mínimo e o máximo do grupo contam o peso de cada opção (sem peso, é a quantidade).
  const somas = groups.map(() => 0);
  const perOption = groups.map(() => new Map<string, number>());
  for (const v of validated) {
    const gi = resolveGroupIndex(groups, v, counts);
    if (gi < 0) continue;
    counts[gi]++;
    somas[gi] += optionWeight(groups[gi], v.optionName);
    perOption[gi].set(v.optionName, (perOption[gi].get(v.optionName) || 0) + 1);
  }
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    const count = counts[i];
    const soma = somas[i];
    if (g.required && count === 0) {
      return `Selecione uma opção em "${g.name}" para ${productName}`;
    }
    const min = groupMinSelections(g);
    if (min > 1 && count > 0 && soma < min - 1e-9) {
      return `Escolha ${min} opções em "${g.name}" para ${productName} (faltam ${formatQtd(min - soma)})`;
    }
    if (min > 1 && count === 0 && !g.required) {
      return `Escolha ${min} opções em "${g.name}" para ${productName}`;
    }
    if (g.selectionType === "single" && count > 1) {
      return `Apenas uma opção é permitida em "${g.name}" para ${productName}`;
    }
    if (g.selectionType === "multiple" && g.maxSelections && soma > g.maxSelections + 1e-9) {
      return groupHasWeights(g)
        ? `"${g.name}" aceita até ${g.maxSelections} para ${productName} (você marcou ${formatQtd(soma)})`
        : `Máximo de ${g.maxSelections} opções em "${g.name}" para ${productName}`;
    }
    if (g.selectionType === "multiple" && g.onePerOption) {
      for (const [optName, n] of perOption[i]) {
        if (n > 1) {
          return `Apenas 1 unidade de "${optName}" é permitida em "${g.name}" para ${productName}`;
        }
      }
    }
  }
  return null;
}

/**
 * UI helper. Same rules as `validateOptionGroupCardinality` but with a
 * selections-by-group representation (used inside the product detail dialog).
 *
 * ⚠️ O record é indexado por POSIÇÃO do grupo (`optionGroupKey`), não pelo
 * nome — nome repete entre grupos e colapsaria a seleção. Ver `resolveGroupIndex`.
 */
export function isValidGroupSelection(
  productGroups: OptionGroup[] | null | undefined,
  selectionsByGroup: Record<string, string[]>,
): boolean {
  const groups = productGroups || [];
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    const sels = selectionsByGroup[optionGroupKey(i)] || [];
    // Mínimo e máximo contam o peso de cada opção (Adicionais fracionados); sem peso, é a quantidade.
    const soma = groupWeightedCount(g, sels);
    if (g.required && sels.length === 0) return false;
    const min = groupMinSelections(g);
    if (min > 1 && sels.length > 0 && soma < min - 1e-9) return false;
    if (min > 1 && !g.required && sels.length === 0) return false;
    if (g.selectionType === "multiple" && g.maxSelections && soma > g.maxSelections + 1e-9) return false;
    if (g.selectionType === "single" && sels.length > 1) return false;
    if (g.selectionType === "multiple" && g.onePerOption && new Set(sels).size !== sels.length) return false;
  }
  return true;
}

/** True se algum adicional do produto tem preço > 0. Usado pra liberar salvar
 * um produto com preço-base 0 (precificado pelos adicionais). */
export function hasPricedOptions(
  productGroups: OptionGroup[] | null | undefined,
): boolean {
  for (const g of productGroups || []) {
    for (const o of g.options || []) {
      const n = Number(o?.price);
      if (Number.isFinite(n) && n > 0) return true;
    }
  }
  return false;
}

// Opção personalizada esgotada (stock 0) não pode ancorar o "A partir de" nem decidir o que forma o preço — o cliente
// não consegue montá-la. (Estoque de ingrediente não chega aqui — mora na tabela ingredients; aceito.)
const opcoesDisponiveis = (g: OptionGroup) => (g.options || []).filter((o) => !(o.stock != null && o.stock <= 0));
const precoDaOpcao = (o: { price?: number | null }): number => {
  const n = Number(o?.price);
  return Number.isFinite(n) ? n : 0;
};
// Grupo com piso > 1 é obrigatório na prática (`validateOptionGroupCardinality` recusa o pedido mesmo sem o
// `required`), então ele entra na conta do piso.
const grupoObrigatorio = (g: OptionGroup) => !!g.required || groupMinSelections(g) > 1;

/** O que o grupo cobra no mínimo montável: as `min` escolhas mais baratas disponíveis, cobradas como o checkout cobra. */
function pisoDoGrupo(g: OptionGroup): number {
  // ⚠️ Somar UMA opção por grupo anunciava preço inalcançável: com piso 2, "a partir de R$ 5,00" nunca fecha — o
  // mínimo montável é R$ 10,00.
  const prices = opcoesDisponiveis(g).map(precoDaOpcao).sort((a, b) => a - b);
  if (prices.length === 0) return 0;
  const min = Math.max(1, groupMinSelections(g));
  // No meio a meio "pelo sabor mais caro" (priceMode highest) vale só a mais cara delas, na "média" a média. Somar
  // sempre anunciava acima do real.
  const escolhidas =
    g.selectionType === "multiple" && g.onePerOption ? prices.slice(0, min) : Array<number>(min).fill(prices[0]!);
  return applyGroupPriceMode(g.selectionType === "multiple" ? g.priceMode : null, escolhidas);
}

/** Preço com a promoção simples em % — o mesmo arredondamento do getActivePromotion (servidor e tela). */
export function precoComPercentual(valor: number, percent: number): number {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  return Math.max(0, Math.round(valor * (1 - pct / 100) * 100) / 100);
}

/**
 * Os grupos que formam o preço de um produto de preço-base 0 (alinhado ao índice de `productGroups`): os obrigatórios
 * com opção disponível, quando o piso deles soma mais que 0; senão (obrigatórios grátis, ou nenhum), todos. É a mesma
 * escolha do "A partir de" (computeStartingFromPrice). Decidido pelo PRODUTO, não pelo que o cliente escolheu.
 */
export function gruposQueFormamOPreco(productGroups: OptionGroup[] | null | undefined): boolean[] {
  const groups = productGroups || [];
  const obrigatorio = groups.map((g) => opcoesDisponiveis(g).length > 0 && grupoObrigatorio(g));
  const piso = groups.reduce((s, g, i) => s + (obrigatorio[i] ? pisoDoGrupo(g) : 0), 0);
  return piso > 0 ? obrigatorio : groups.map(() => true);
}

/** A promoção simples que vai para as opções de um produto de preço-base 0: em % ou de valor fixo X (o preço da
 * opção vira min(preço, X), como o min(base, X) do produto com preço-base). Número puro = % (como era até 24/10). */
export type PromocaoNasOpcoes = { tipo: "percent" | "fixed"; valor: number };

function regraDasOpcoes(regra: PromocaoNasOpcoes | number | null | undefined): PromocaoNasOpcoes | null {
  if (regra == null) return null;
  const r = typeof regra === "number" ? { tipo: "percent" as const, valor: regra } : regra;
  const valor = Number(r.valor);
  if (!Number.isFinite(valor)) return null;
  if (r.tipo === "percent") return valor > 0 ? { tipo: "percent", valor } : null;
  return { tipo: "fixed", valor: Math.max(0, valor) };
}

/** O preço de uma opção (ou do «A partir de» manual) com a promoção nas opções — % ou valor fixo, o mesmo arredondamento
 * do getActivePromotion. Sem regra → o próprio valor. */
export function precoComPromocaoNasOpcoes(valor: number, regra: PromocaoNasOpcoes | number | null | undefined): number {
  const r = regraDasOpcoes(regra);
  if (!r) return valor;
  if (r.tipo === "percent") return precoComPercentual(valor, r.valor);
  return Math.max(0, Math.round(Math.min(valor, r.valor) * 100) / 100);
}

/**
 * Promoção simples num produto de preço-base 0 (Pedro, 23/10/2026: «Se tem uma configuração, o sistema inteiro tem que
 * seguir isso»; o valor fixo em 24/10): a cópia dos grupos com a promoção já aplicada no preço de cada opção dos grupos
 * que formam o preço (gruposQueFormamOPreco) — o % no preço, ou o valor fixo X como teto (min(preço, X)); os extras
 * ficam cheios. Servidor (pickAndPriceOptions) e tela (modal, carrinho, "A partir de") precificam por estes grupos — a
 * mesma conta dos dois lados, até no arredondamento. Sem regra (null, ou % ≤ 0) → os mesmos grupos. Quem chama decide
 * se a promoção está ativa (agenda, canal) e se o preço-base é ≤ 0.
 */
export function opcoesComPromocao<G extends OptionGroup>(
  productGroups: G[] | null | undefined,
  regra: PromocaoNasOpcoes | number | null | undefined,
): G[] {
  const groups = productGroups || [];
  const r = regraDasOpcoes(regra);
  if (!r || groups.length === 0) return groups;
  const formam = gruposQueFormamOPreco(groups);
  return groups.map((g, i) =>
    formam[i]
      ? {
          ...g,
          options: (g.options || []).map((o) => {
            const n = Number(o?.price);
            return Number.isFinite(n) ? { ...o, price: precoComPromocaoNasOpcoes(n, r) } : o;
          }),
        }
      : g,
  );
}

/**
 * Preço "A partir de" pra produtos com preço-base 0 (precificados pelos adicionais).
 * Prioridade:
 *  1. override manual do lojista (`startingPriceOverride`), se > 0;
 *  2. mínimo real: soma do que CADA grupo obrigatório custa no piso — o
 *     cliente é forçado a `groupMinSelections` escolhas nele (N × a mais
 *     barata, ou a soma das N mais baratas distintas com `onePerOption`);
 *  3. sem grupo obrigatório (ou mínimo obrigatório 0): o adicional pago mais barato.
 * Retorna null quando não há valor a exibir (nenhum adicional pago).
 */
export function computeStartingFromPrice(
  productGroups: OptionGroup[] | null | undefined,
  startingPriceOverride?: number | string | null,
): number | null {
  const override = Number(startingPriceOverride);
  if (Number.isFinite(override) && override > 0) {
    return Math.round(override * 100) / 100;
  }

  const groups = (productGroups || []).filter((g) => opcoesDisponiveis(g).length > 0);
  if (groups.length === 0) return null;

  const requiredGroups = groups.filter(grupoObrigatorio);
  if (requiredGroups.length > 0) {
    const sum = requiredGroups.reduce((s, g) => s + pisoDoGrupo(g), 0);
    if (sum > 0) return Math.round(sum * 100) / 100;
    // Todos os obrigatórios têm opção grátis → cai pro adicional pago mais barato.
  }

  const paid = groups
    .flatMap((g) => opcoesDisponiveis(g).map(precoDaOpcao))
    .filter((p) => p > 0);
  if (paid.length === 0) return null;
  return Math.round(Math.min(...paid) * 100) / 100;
}

export interface ValidatedRemovedIngredients {
  validated: string[];
  /** Pedidos de remoção que não existem mais na lista do produto. */
  unmatched: string[];
}

/**
 * "Sem cebola" do carrinho contra a lista de removíveis do produto.
 *
 * ⚠️ Compara com `foldOptionName` (caixa/acento), igual aos adicionais: o
 * carrinho fica no localStorage sem prazo e o lojista renomeia a lista, então
 * "Cebola" vs "cebola" descartava o pedido do cliente CALADO — ele recebia o
 * item com o ingrediente. Grava a grafia da LISTA (a do lojista hoje), não a
 * do carrinho.
 */
export function validateRemovedIngredientsDetailed(
  productIngredients: string[] | null | undefined,
  removed: string[] | null | undefined,
): ValidatedRemovedIngredients {
  if (!Array.isArray(removed) || !Array.isArray(productIngredients)) {
    return { validated: [], unmatched: [] };
  }
  const validated: string[] = [];
  const unmatched: string[] = [];
  for (const ing of removed) {
    const exato = productIngredients.find((i) => i === ing);
    if (exato !== undefined) {
      validated.push(exato);
      continue;
    }
    const alvo = foldOptionName(ing);
    const canonico = productIngredients.find((i) => foldOptionName(i) === alvo);
    if (canonico !== undefined) validated.push(canonico);
    else unmatched.push(ing);
  }
  return { validated, unmatched };
}

export function validateRemovedIngredients(
  productIngredients: string[] | null | undefined,
  removed: string[] | null | undefined,
): string[] {
  return validateRemovedIngredientsDetailed(productIngredients, removed).validated;
}
